import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Globe, Lock, RefreshCcw, ScanLine, BookOpen, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import TagoraSeal from '../components/TagoraSeal';

const Label = () => {
  const principles = [
    { icon: Lock, title: 'Anonymat absolu', desc: "Aucune donnee personnelle n'est revelee au scanneur. Le proprietaire reste totalement anonyme jusqu'a ce qu'il decide de repondre." },
    { icon: RefreshCcw, title: 'Reversibilite', desc: 'Vous pouvez transferer, reactiver ou desactiver votre Tagora ID a tout moment. Vous gardez le controle.' },
    { icon: Globe, title: 'Interoperabilite', desc: 'Aucune application requise. Tout lecteur QR du marche fonctionne sur n\'importe quel smartphone, dans le monde entier.' },
    { icon: BookOpen, title: 'Specification ouverte', desc: 'Le format Tagora ID est documente publiquement. Toute marque ou createur peut l\'integrer librement dans ses produits.' },
    { icon: ShieldCheck, title: 'Audit & traçabilite', desc: 'Chaque scan est consigne pour le proprietaire (geolocalisation approximative, horodatage). RGPD-compliant.' },
    { icon: ScanLine, title: 'Verification universelle', desc: 'N\'importe qui peut verifier l\'authenticite d\'un code Tagora ID en scannant ou en saisissant son numero.' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="px-6 sm:px-8 lg:px-12 pt-24 pb-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3 space-y-6">
              <div className="inline-flex items-center gap-2 bg-accent border-2 border-charcoal rounded-full px-4 py-2 shadow-brutal text-sm font-bold">
                <ShieldCheck className="w-4 h-4" strokeWidth={2.5} /> Standard ouvert
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-none text-charcoal font-heading">
                Le standard d'identification anonyme<br />
                <span className="text-primary">des objets connectes.</span>
              </h1>
              <p className="text-lg text-charcoal/80 max-w-2xl font-body leading-relaxed">
                <strong>Tagora ID</strong> est un identifiant universel qui relie un objet a son proprietaire sans jamais reveler son identite. Pense pour devenir une norme internationale, comme l'ont ete <em>GS1</em> pour les produits ou <em>ISBN</em> pour les livres.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register" className="btn-primary inline-flex items-center gap-2">
                  Obtenir un Tagora ID <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/verify" className="btn-outline inline-flex items-center gap-2" data-testid="verify-cta">
                  <ScanLine className="w-4 h-4" /> Verifier un code
                </Link>
              </div>
            </div>
            <div className="lg:col-span-2 flex justify-center">
              <TagoraSeal tagoraId="TGR-7K2L-9P3M-A4B5-X9Y2" variant="full" verified />
            </div>
          </div>
        </div>
      </section>

      {/* Format spec */}
      <section className="px-6 sm:px-8 lg:px-12 py-16 bg-charcoal text-white">
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-3">Specification</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight font-heading mb-8">Format de l'identifiant</h2>
          <div className="bg-white text-charcoal border-2 border-white rounded-2xl p-8 shadow-brutal-lg">
            <div className="text-center mb-6">
              <code className="text-2xl sm:text-4xl font-mono font-bold tracking-wider">
                TGR-<span className="text-primary">XXXX</span>-<span className="text-primary">XXXX</span>-<span className="text-primary">XXXX</span>-<span className="text-primary">XXXY</span>
              </code>
            </div>
            <div className="grid sm:grid-cols-2 gap-6 text-sm">
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-charcoal/50 mb-1">Prefixe</p>
                <p><code className="font-mono font-bold">TGR</code> — Signature Tagora</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-charcoal/50 mb-1">Caracteres</p>
                <p>16 caracteres Crockford Base32 (sans O, I, L, U)</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-charcoal/50 mb-1">Espace</p>
                <p>32<sup>15</sup> ≈ <strong>3,7 × 10<sup>22</sup></strong> codes uniques</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-charcoal/50 mb-1">Checksum</p>
                <p>Le caractere <code className="font-mono">Y</code> est un mod-37 — verification offline</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="px-6 sm:px-8 lg:px-12 py-20">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-charcoal mb-3">Manifeste</p>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-charcoal font-heading mb-12">Les 6 principes Tagora ID</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {principles.map((p, i) => (
              <div key={i} className="card-brutal hover:shadow-brutal-xl hover:-translate-y-1 transition-all p-6">
                <div className="bg-primary w-12 h-12 rounded-xl border-2 border-charcoal flex items-center justify-center mb-4">
                  <p.icon className="w-6 h-6 text-charcoal" strokeWidth={2.5} />
                </div>
                <h3 className="text-lg font-bold text-charcoal mb-2 font-heading">{p.title}</h3>
                <p className="text-sm text-charcoal/70 font-body">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 sm:px-8 lg:px-12 py-20">
        <div className="max-w-4xl mx-auto">
          <div className="card-brutal bg-primary/20 text-center p-12">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-charcoal font-heading mb-4">
              Adoptez le standard.
            </h2>
            <p className="text-lg text-charcoal/80 mb-6 font-body">
              Que vous soyez particulier ou marque, donnez a vos objets une identite anonyme et internationale.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/register" className="btn-primary inline-flex items-center gap-2">
                Creer un compte <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/verify" className="btn-outline inline-flex items-center gap-2">
                <ScanLine className="w-4 h-4" /> Verifier un code
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Label;
